import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Attachment from '@common/components/attachment';
import Dropdown from '@common/components/dropdown';
import Icon from '@common/components/icon';
import Confirm from '@common/components/confirm-button';

// Services
import * as AnalyticsService from '@common/services/analytics';

// Types
import { EFileTypes, FileTypes } from '@common/definitions';
import { Attachment as AttachmentType } from '@common/types/objects';
import { EEventNames } from '../../../../../client/analytics';


const shouldShowPreview = (attachment: AttachmentType) => (
  !!attachment?.file_type && ([EFileTypes.IMAGE, EFileTypes.GIF, EFileTypes.VIDEO] as FileTypes[]).includes(attachment.file_type)
);

interface MessageAttachmentProps {
  attachment: AttachmentType;
  timestamp: string;
  conversationId: string;
  showActions: boolean;
  canDelete: boolean;
  onRemove: () => void;
  // MessageActions component as a prop
  MessageActionsComponent?: React.ComponentType<any>;
  // Optional props for MessageActions
  messageActionsProps?: any;
}

const MessageAttachment = ({
  attachment, timestamp, conversationId, showActions, canDelete,
  onRemove, MessageActionsComponent, messageActionsProps,
}: MessageAttachmentProps) => {
  const { t } = useTranslation();
  const isPreview = useMemo(() => shouldShowPreview(attachment), [attachment]);

  // Independent state management for each attachment
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  return (
    <div className="PrivateMessage__Container PrivateMessage__Container--preview tw-w-full" key={attachment.id}>
      <div className={`${isPreview ? 'tw-relative' : 'PrivateMessage__Inner'}`}>
        <Attachment
          key={attachment.id}
          item={attachment}
          maxHeight={300}
          label={timestamp}
          onOpen={() => AnalyticsService.track(EEventNames.OPENED_FILE_IN_CHAT, { conversationId })}
          includeLoader
          preview={attachment.file_type === EFileTypes.VIDEO}
        />
        {!isPreview && <small className="PrivateMessage__timestamp">{timestamp}</small>}
        {showActions && (
          <>
            {MessageActionsComponent ? (
              <MessageActionsComponent
                {...messageActionsProps}
                setDropdownOpen={setIsDropdownOpen}
                isDropdownOpen={isDropdownOpen}
                isAttachment
              />
            ) : (
              <Dropdown
                alignRight
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                toggle={(
                  <div className={`${isPreview ? 'PrivateMessage__Options--preview' : 'PrivateMessage__Options'}`}>
                    <Icon type="more_vert" />
                  </div>
                )}
              >
                {canDelete && (
                  <Confirm
                    title={t('chat:confirm_remove_message')}
                    onConfirm={() => onRemove()}
                  >
                    <Dropdown.Item danger>
                      {t('chat:remove_message')}
                    </Dropdown.Item>
                  </Confirm>
                )}
              </Dropdown>
            )}
          </>
        )}
        {isPreview && <div className="PrivateMessage__AttachmentDivider" />}
      </div>
    </div>
  );
};

export default MessageAttachment;
